import { FelaCSS, FelaCSSWithCustomSelectors, colors, spacing } from '@bridebook/ui';
import rgbToRgba from '@bridebook/ui/src/utils/rgb-to-rgba';
import {
  PALETTE_BACKGROUNDS,
  PALETTE_EXTRA_ASSETS,
} from '@bridebook/web/lib/wedding-website/constants/themes';
import { Palettes } from '@bridebook/web/lib/wedding-website/types';
import { buildImageSrc } from '@bridebook/toolbox/src/buildImageUrl';

interface IStyles {
  wrapper: (palette: Palettes) => FelaCSS;
  fadingOverlay: FelaCSS;
  contentWrapper: FelaCSS;
  container: FelaCSS;
  heading: FelaCSSWithCustomSelectors;
  passwordWrapper: FelaCSS;
  passwordContainer: FelaCSS;
  button: FelaCSS;

  paletteRosesAsset: (corner: 'top-left' | 'bottom-right', palette: Palettes) => FelaCSS;
  paletteFoliageAsset: (position: 'left' | 'right', palette: Palettes) => FelaCSS;
  paletteWhimsicalAsset: (position: 'left' | 'right', palette: Palettes) => FelaCSS;
}

const THEME_BACKGROUNDS: Record<Palettes, FelaCSS> = {
  [Palettes.Bridebook]: {
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundImage: `url(${PALETTE_BACKGROUNDS[Palettes.Bridebook].tileBackground})`,
  },
  [Palettes.Roses]: {
    backgroundColor: 'rgba(251, 251, 246, 1)',
  },
  [Palettes.Foliage]: {
    backgroundColor: 'rgba(239, 248, 242, 1)',
  },
  [Palettes.Whimsical]: {
    backgroundColor: 'rgba(220, 146, 169, 1)',
  },
};

const styles: IStyles = {
  wrapper: (palette: Palettes) => ({
    ...THEME_BACKGROUNDS[palette],
    justifyContent: palette === Palettes.Roses ? 'center' : 'end',
    flexGrow: 1,

    minWidthDesktop: {
      flexDirection: 'row',
    },
  }),
  fadingOverlay: {
    display: 'none',

    minWidthDesktop: {
      display: 'flex',
      flexGrow: 1,
      flexShrink: 1,
      backgroundColor: rgbToRgba(colors.white, 0.4),
      pointerEvents: 'none',
    },
  },
  contentWrapper: {
    height: '100%',
    gap: spacing[16],
    paddingBottom: spacing[16],
    paddingTop: spacing[16],
    justifyContent: 'end',
    minWidthDesktop: {
      maxWidth: 620,
      alignSelf: 'center',
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: spacing[0],
    },
  },
  container: {
    paddingHorizontal: spacing[4],
    gap: spacing[2],
    alignItems: 'center',
    textAlign: 'center',
  },
  heading: {
    paddingHorizontal: spacing[6],
    textAlign: 'center',
    wordBreak: 'break-word',
    fontSize: '36px',
    fontWeight: 800,
    flexDirection: 'row',
    '@container (width > 768px)': {
      fontSize: '48px',
    },

    minWidthDesktop: {
      paddingHorizontal: spacing[0],
    },
  },
  passwordWrapper: {
    paddingHorizontal: spacing[4],
    alignItems: 'center',
    gap: spacing[4],
    width: '100%',
  },
  passwordContainer: {
    width: '100%',
    maxWidth: 343,
  },
  button: {
    marginTop: spacing[4],
    minWidthDesktop: {
      marginTop: spacing[12],
    },
  },

  paletteRosesAsset: (corner: 'top-left' | 'bottom-right', palette: Palettes) => ({
    display: palette !== Palettes.Roses ? 'none' : undefined,
    position: 'absolute',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundImage: `url(${buildImageSrc(PALETTE_EXTRA_ASSETS[Palettes.Roses], { h: 300 })})`,
    width: 76,
    height: 114,
    ...(corner === 'top-left'
      ? {
          top: spacing[4],
          left: spacing[5],
        }
      : {
          bottom: spacing[4],
          right: spacing[5],
          transform: 'rotate(180deg)',
        }),

    containerMinWidthDesktop: {
      width: 140,
      height: 210,
    },
  }),
  paletteFoliageAsset: (position: 'left' | 'right', palette: Palettes) => ({
    display: palette !== Palettes.Foliage ? 'none' : undefined,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundImage: `url(${buildImageSrc(PALETTE_EXTRA_ASSETS[Palettes.Foliage], {
      h: 300,
    })})`,
    transform: `scaleX(${position === 'left' ? '1' : '-1'})`,
    width: 80,
    minWidth: 80,
    height: 120,

    containerMinWidthDesktop: {
      width: 124,
      minWidth: 124,
      height: 186,
    },
  }),
  paletteWhimsicalAsset: (position: 'left' | 'right', palette: Palettes) => ({
    display: palette !== Palettes.Whimsical ? 'none' : undefined,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundImage: `url(${buildImageSrc(PALETTE_EXTRA_ASSETS[Palettes.Whimsical], {
      h: 300,
    })})`,
    transform: `scaleX(${position === 'left' ? '1' : '-1'})`,
    width: 65,
    minWidth: 65,
    height: 120,

    containerMinWidthDesktop: {
      width: 105,
      minWidth: 105,
      height: 190,
    },
  }),
};

export default styles;
